import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for error tracking
 * Only runs in production with valid DSN
 */
export const initializeSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  // Only initialize if DSN is configured
  if (!dsn || import.meta.env.DEV) {
    console.log('Sentry: Not initialized (development mode or missing DSN)');
    return;
  }

  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Performance Monitoring
    tracesSampleRate: 0.1, // 10% of transactions for performance
    
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Environment
    environment: import.meta.env.MODE,
    
    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || 'development',
    
    // Filter out non-critical errors
    beforeSend(event, hint) {
      // Don't send if it's a known browser extension error
      if (event.exception?.values?.[0]?.value?.includes('extension')) {
        return null;
      }
      
      // Don't send network errors we can't control
      if (event.exception?.values?.[0]?.type === 'NetworkError') {
        return null;
      }
      
      return event;
    },
  });

  console.log('Sentry: Initialized successfully');
};

/**
 * Manually capture an exception
 */
export const captureException = (error: Error, context?: Record<string, any>) => {
  if (import.meta.env.DEV) {
    console.error('Error (not sent to Sentry in dev):', error, context);
    return;
  }
  
  Sentry.captureException(error, {
    extra: context,
  });
};

/**
 * Manually capture a message
 */
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  if (import.meta.env.DEV) {
    console.log(`Message (not sent to Sentry in dev) [${level}]:`, message);
    return;
  }
  
  Sentry.captureMessage(message, level);
};

/**
 * Set user context for error tracking
 */
export const setUserContext = (user: { id: string; email?: string; org_id?: string }) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    org_id: user.org_id,
  });
};

/**
 * Clear user context (on logout)
 */
export const clearUserContext = () => {
  Sentry.setUser(null);
};
